import { Link } from 'react-router';
import { cn } from '../../../lib/utils';
import { formatNullableDate } from '../reportingUtils';
import type {
  ReportingBackendIngestorFailure,
  ReportingBackendIngestorMetrics,
} from '../types';
import {
  RatioBar,
  ReportingInset,
  ReportingPanel,
  ReportingSectionHeader,
  ReportingStatusPill,
} from './ReportingChrome';
import { ReportingMetricCard } from './ReportingMetricCard';

interface ExternalMetricsSectionProps {
  backendIngestor: ReportingBackendIngestorMetrics;
}

const OUTCOME_STYLES = {
  completed: {
    label: 'Completed',
    bar: 'bg-teal-400/70',
    text: 'text-teal-300',
    tone: 'teal' as const,
  },
  errored: {
    label: 'Errored',
    bar: 'bg-rose-400/70',
    text: 'text-rose-300',
    tone: 'rose' as const,
  },
  processed: {
    label: 'Processed',
    bar: 'bg-sky-400/60',
    text: 'text-sky-300',
    tone: 'neutral' as const,
  },
  unknown: {
    label: 'Unknown',
    bar: 'bg-neutral-500/60',
    text: 'text-neutral-300',
    tone: 'neutral' as const,
  },
};

export function ExternalMetricsSection({
  backendIngestor,
}: ExternalMetricsSectionProps) {
  const { summary } = backendIngestor;
  const latestDaily = backendIngestor.daily.slice(-30);

  return (
    <div className="space-y-6">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <ReportingMetricCard
          label="Ingestor Success"
          value={formatPercent(summary.successRate)}
          tone={summary.successRate === null ? 'amber' : 'teal'}
          emphasis="highlight"
          hint={`${summary.completedTickets.toLocaleString()} completed of ${summary.terminalTickets.toLocaleString()} terminal tickets`}
          footer={
            <RatioBar
              numerator={summary.completedTickets}
              denominator={summary.terminalTickets}
              numeratorLabel="Complete"
              denominatorLabel="Terminal"
            />
          }
        />
        <ReportingMetricCard
          label="Failure Rate"
          value={formatPercent(summary.failureRate)}
          tone={summary.erroredTickets > 0 ? 'amber' : 'teal'}
          emphasis="highlight"
          hint={`${summary.erroredTickets.toLocaleString()} latest-error tickets, ${summary.ticketsWithAnyError.toLocaleString()} touched by errors`}
          footer={
            <RatioBar
              numerator={summary.erroredTickets}
              denominator={summary.terminalTickets}
              numeratorLabel="Errored"
              denominatorLabel="Terminal"
            />
          }
        />
        <ReportingMetricCard
          label="Tickets Observed"
          value={summary.uniqueTickets.toLocaleString()}
          hint={`${summary.totalEvents.toLocaleString()} ingestor events in range`}
          footer={
            <RatioBar
              numerator={summary.processedTickets}
              denominator={summary.uniqueTickets}
              numeratorLabel="In progress"
              denominatorLabel="Observed"
            />
          }
        />
        <ReportingMetricCard
          label="Retry Volume"
          value={summary.retriedTickets.toLocaleString()}
          tone={summary.retriedTickets > 0 ? 'amber' : 'teal'}
          hint={`Average ${formatNullableNumber(summary.averageEventsPerTicket)} events per ticket`}
          footer={
            <RatioBar
              numerator={summary.retriedTickets}
              denominator={summary.uniqueTickets}
              numeratorLabel="Retried"
              denominatorLabel="Observed"
            />
          }
        />
      </div>

      <ReportingPanel className="overflow-hidden">
        <div className="p-5">
          <ReportingSectionHeader
            title="Backend ingestor"
            subtitle="HubSpot ticket-processing telemetry from external metrics ingestion"
            trailing={
              <div className="flex flex-wrap justify-end gap-2">
                <ReportingStatusPill
                  label={summary.hasData ? 'Receiving data' : 'No range data'}
                  tone={summary.hasData ? 'teal' : 'amber'}
                />
                {summary.environment && (
                  <ReportingStatusPill label={summary.environment} />
                )}
                {summary.serviceName && (
                  <ReportingStatusPill label={summary.serviceName} />
                )}
              </div>
            }
          />

          <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
            <ReportingInset className="p-4">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h4 className="text-sm font-medium text-neutral-200">
                      Latest ticket outcomes
                    </h4>
                    <p className="text-xs text-neutral-500">
                      Each ticket is counted by its latest ingestor outcome in
                      range.
                    </p>
                  </div>
                  <span className="text-xs tabular-nums text-neutral-500">
                    Last event {formatNullableDate(summary.lastObservedAt, '—')}
                  </span>
                </div>
                <OutcomeStack
                  completed={summary.completedTickets}
                  errored={summary.erroredTickets}
                  processed={summary.processedTickets}
                  unknown={summary.unknownTickets}
                />
                <OutcomeBreakdown metrics={backendIngestor} />
              </div>
            </ReportingInset>

            <ReportingInset className="p-4">
              <div className="grid grid-cols-2 gap-3">
                <OperationalStat
                  label="First event"
                  value={formatNullableDate(summary.firstObservedAt, '—')}
                />
                <OperationalStat
                  label="Latest event"
                  value={formatNullableDate(summary.lastObservedAt, '—')}
                />
                <OperationalStat
                  label="Median terminal span"
                  value={formatDuration(summary.medianSecondsToTerminal)}
                />
                <OperationalStat
                  label="P95 terminal span"
                  value={formatDuration(summary.p95SecondsToTerminal)}
                />
              </div>
              <div className="mt-4 rounded-md border border-neutral-800/70 bg-neutral-950/40 p-2.5">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500">
                  Event status mix
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <EventCount
                    label="Complete"
                    value={summary.completeEvents}
                    className="text-teal-300"
                  />
                  <EventCount
                    label="Error"
                    value={summary.errorEvents}
                    className="text-rose-300"
                  />
                  <EventCount
                    label="Processed"
                    value={summary.processedEvents}
                    className="text-sky-300"
                  />
                </div>
              </div>
            </ReportingInset>
          </div>
        </div>
      </ReportingPanel>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(420px,0.9fr)]">
        <ReportingPanel className="overflow-hidden">
          <div className="p-5">
            <ReportingSectionHeader
              title="Daily ingestor trend"
              subtitle="Latest status per ticket per day"
            />
            <DailyTrend daily={latestDaily} />
          </div>
        </ReportingPanel>

        <ReportingPanel className="overflow-hidden">
          <div className="p-5">
            <ReportingSectionHeader
              title="Recent failed tickets"
              subtitle="Tickets whose latest ingestor outcome is Error"
            />
            <RecentFailures failures={backendIngestor.recentFailures} />
          </div>
        </ReportingPanel>
      </div>
    </div>
  );
}

function OutcomeStack({
  completed,
  errored,
  processed,
  unknown,
}: {
  completed: number;
  errored: number;
  processed: number;
  unknown: number;
}) {
  const total = completed + errored + processed + unknown;
  const segments = [
    ['completed', completed],
    ['errored', errored],
    ['processed', processed],
    ['unknown', unknown],
  ] as const;

  if (total === 0) {
    return (
      <div className="rounded-xl border border-neutral-800/40 bg-neutral-950/30 px-3 py-6 text-center text-sm text-neutral-500">
        No ingestor events were recorded in this range.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex h-2 overflow-hidden rounded-full bg-neutral-800/60">
        {segments.map(([outcome, value]) => {
          if (value === 0) {
            return null;
          }

          return (
            <span
              key={outcome}
              className={OUTCOME_STYLES[outcome].bar}
              style={{ width: `${(value / total) * 100}%` }}
            />
          );
        })}
      </div>
      <div className="grid gap-2 sm:grid-cols-4">
        {segments.map(([outcome, value]) => (
          <div
            key={outcome}
            className="rounded-md border border-neutral-800/70 bg-neutral-950/40 px-2.5 py-2"
          >
            <div className="text-[11px] uppercase tracking-wider text-neutral-500">
              {OUTCOME_STYLES[outcome].label}
            </div>
            <div
              className={cn(
                'mt-0.5 text-base font-semibold tabular-nums',
                OUTCOME_STYLES[outcome].text,
              )}
            >
              {value.toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OutcomeBreakdown({
  metrics,
}: {
  metrics: ReportingBackendIngestorMetrics;
}) {
  if (metrics.outcomeBreakdown.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {metrics.outcomeBreakdown.map((row) => (
        <div
          key={row.outcome}
          className="flex items-center justify-between gap-3 text-xs"
        >
          <div className="flex min-w-0 items-center gap-2">
            <ReportingStatusPill
              label={OUTCOME_STYLES[row.outcome].label}
              tone={OUTCOME_STYLES[row.outcome].tone}
            />
            <span className="truncate text-neutral-500">
              {row.events.toLocaleString()} events
            </span>
          </div>
          <div className="text-right tabular-nums text-neutral-300">
            {row.tickets.toLocaleString()} tickets
          </div>
        </div>
      ))}
    </div>
  );
}

function OperationalStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-neutral-800/70 bg-neutral-950/40 p-2.5">
      <div className="text-[11px] uppercase tracking-wider text-neutral-500">
        {label}
      </div>
      <div className="mt-0.5 text-sm font-medium tabular-nums text-neutral-200">
        {value}
      </div>
    </div>
  );
}

function EventCount({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className?: string;
}) {
  return (
    <div>
      <div className={cn('text-sm font-medium tabular-nums', className)}>
        {value.toLocaleString()}
      </div>
      <div className="text-[11px] text-neutral-500">{label}</div>
    </div>
  );
}

function DailyTrend({
  daily,
}: {
  daily: ReportingBackendIngestorMetrics['daily'];
}) {
  if (daily.length === 0) {
    return (
      <p className="mt-4 text-sm text-neutral-500">
        No daily ingestor data is available for this range.
      </p>
    );
  }

  const peak = Math.max(...daily.map((point) => point.ticketsTouched), 1);

  return (
    <div className="mt-4 max-h-[32rem] space-y-2 overflow-y-auto pr-1">
      {daily.map((point) => (
        <DailyTrendRow key={point.date} point={point} peak={peak} />
      ))}
    </div>
  );
}

function DailyTrendRow({
  point,
  peak,
}: {
  point: ReportingBackendIngestorMetrics['daily'][number];
  peak: number;
}) {
  const dateLabel = new Date(point.date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const total = Math.max(point.ticketsTouched, 1);
  const visibleWidth = Math.max(
    point.ticketsTouched === 0 ? 0 : 6,
    Math.round((point.ticketsTouched / peak) * 100),
  );

  return (
    <div className="grid grid-cols-[4.5rem_minmax(0,1fr)_4rem] items-center gap-3 text-xs">
      <div className="tabular-nums text-neutral-500">{dateLabel}</div>
      <div className="h-1.5 overflow-hidden rounded-full bg-neutral-800/60">
        <div
          className="flex h-full overflow-hidden rounded-full"
          style={{ width: `${visibleWidth}%` }}
        >
          <span
            className="bg-teal-400/70"
            style={{
              width: `${(point.completedTickets / total) * 100}%`,
            }}
          />
          <span
            className="bg-rose-400/70"
            style={{
              width: `${(point.erroredTickets / total) * 100}%`,
            }}
          />
          <span
            className="bg-sky-400/60"
            style={{
              width: `${(point.processedTickets / total) * 100}%`,
            }}
          />
          <span
            className="bg-neutral-500/60"
            style={{
              width: `${(point.unknownTickets / total) * 100}%`,
            }}
          />
        </div>
      </div>
      <div className="text-right tabular-nums text-neutral-400">
        {point.ticketsTouched.toLocaleString()}
      </div>
    </div>
  );
}

function RecentFailures({
  failures,
}: {
  failures: ReportingBackendIngestorFailure[];
}) {
  if (failures.length === 0) {
    return (
      <p className="mt-4 text-sm text-neutral-500">
        No failed backend-ingestor tickets are current in this range.
      </p>
    );
  }

  return (
    <div className="mt-4 space-y-2">
      {failures.map((failure) => (
        <FailureRow key={failure.ticketId} failure={failure} />
      ))}
    </div>
  );
}

function FailureRow({ failure }: { failure: ReportingBackendIngestorFailure }) {
  const ticketLabel = `Ticket ${failure.ticketId}`;
  const attemptsLabel =
    failure.errorEvents === failure.eventCount
      ? `${failure.errorEvents.toLocaleString()} error events`
      : `${failure.errorEvents.toLocaleString()} errors / ${failure.eventCount.toLocaleString()} events`;

  return (
    <ReportingInset className="p-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {failure.portalTicketId ? (
              <Link
                to={`/tickets?ticketId=${failure.portalTicketId}`}
                className="truncate text-sm font-medium text-teal-200 hover:text-teal-100"
              >
                {ticketLabel}
              </Link>
            ) : (
              <span className="truncate text-sm font-medium text-white">
                {ticketLabel}
              </span>
            )}
            <ReportingStatusPill label="Error" tone="rose" />
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-neutral-500">
            {failure.requestType && <span>{failure.requestType}</span>}
            {failure.ticketType && <span>{failure.ticketType}</span>}
            {failure.mappedSubmissionStatus && (
              <span>Portal {failure.mappedSubmissionStatus}</span>
            )}
            {failure.isComplete !== null && (
              <span>{failure.isComplete ? 'Complete' : 'Incomplete'}</span>
            )}
          </div>
        </div>
        <div className="shrink-0 text-left sm:text-right">
          <div className="text-xs tabular-nums text-neutral-300">
            {formatNullableDate(failure.latestObservedAt, '—')}
          </div>
          <div className="mt-0.5 text-[11px] text-neutral-500">
            {attemptsLabel}
          </div>
        </div>
      </div>
    </ReportingInset>
  );
}

function formatPercent(value: number | null) {
  return value === null ? '—' : `${value.toFixed(1)}%`;
}

function formatNullableNumber(value: number | null) {
  return value === null ? '—' : value.toFixed(2);
}

function formatDuration(seconds: number | null) {
  if (seconds === null) {
    return '—';
  }

  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  }

  if (seconds < 3600) {
    return `${Math.round(seconds / 60)}m`;
  }

  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  const days = Math.floor(seconds / 86400);
  const hours = Math.round((seconds % 86400) / 3600);
  return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
}
